import { useEffect, useState } from 'react';
import api from '../api/axios';

interface POOrder {
  id: number;
  group_id: string | null;
  customer_name: string;
  customer_phone: string;
  inventory_id: number;
  product_name: string;
  sku: string;
  selling_price: number;
  quantity: number;
  note: string | null;
  status: 'menunggu' | 'diproses' | 'siap' | 'selesai' | 'dibatalkan';
  purchase_id: number | null;
  sale_id: number | null;
  created_at: string;
}

interface EditForm {
  customer_name: string;
  customer_phone: string;
  quantity: number;
  note: string;
}

const rupiahFormat = (n: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

const STATUS_CONFIG = {
  menunggu:  { label: 'Menunggu',   color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  diproses:  { label: 'Diproses',   color: 'bg-blue-100 text-blue-700 border-blue-200' },
  siap:      { label: 'Siap Kirim', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  selesai:   { label: 'Selesai',    color: 'bg-gray-100 text-gray-600 border-gray-200' },
  dibatalkan:{ label: 'Dibatalkan', color: 'bg-red-100 text-red-600 border-red-200' },
};

const WA_SELESAI = (name: string, product: string, qty: number) =>
  `Halo ${name}! 👋\n\nPesanan PO Anda telah selesai diproses:\n📦 Produk: ${product}\n🔢 Qty: ${qty} pcs\n\nBarang sedang dalam perjalanan ke lokasi Anda.\nTerima kasih sudah berbelanja di TemanSkrispy! 🙏`;

const WA_BATAL = (name: string, product: string) =>
  `Halo ${name},\n\nMohon maaf, pesanan PO Anda untuk *${product}* tidak dapat kami proses saat ini karena stok tidak tersedia di supplier.\n\nKami akan menginformasikan kembali jika stok sudah tersedia. 🙏\n\nTerima kasih atas pengertiannya.`;

const formatPhone = (phone: string) => {
  let p = phone.replace(/\D/g, '');
  if (p.startsWith('0')) p = '62' + p.slice(1);
  if (!p.startsWith('62')) p = '62' + p;
  return p;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

type FilterStatus = 'all' | POOrder['status'];

export default function PesananPO() {
  const [orders, setOrders] = useState<POOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');

  // Edit modal
  const [editOrder, setEditOrder] = useState<POOrder | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ customer_name: '', customer_phone: '', quantity: 1, note: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Confirm action modal
  const [confirmOrder, setConfirmOrder] = useState<POOrder | null>(null);
  const [confirmAction, setConfirmAction] = useState<'diproses' | 'siap' | 'selesai' | 'dibatalkan' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const fetchOrders = async () => {
    try {
      const res = await api.get('/po-orders/getAll');
      setOrders(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const filtered = orders.filter((o) => filterStatus === 'all' || o.status === filterStatus);

  const openEdit = (order: POOrder) => {
    setEditOrder(order);
    setEditForm({ customer_name: order.customer_name, customer_phone: order.customer_phone, quantity: order.quantity, note: order.note || '' });
    setEditError('');
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editOrder) return;
    setEditSaving(true);
    setEditError('');
    try {
      await api.put(`/po-orders/update/${editOrder.id}`, editForm);
      setEditOrder(null);
      fetchOrders();
    } catch (err: any) {
      setEditError(err.response?.data?.message || 'Gagal menyimpan perubahan');
    } finally {
      setEditSaving(false);
    }
  };

  const openConfirm = (order: POOrder, action: typeof confirmAction) => {
    setConfirmOrder(order);
    setConfirmAction(action);
    setActionError('');
  };

  const handleConfirmAction = async () => {
    if (!confirmOrder || !confirmAction) return;
    setActionLoading(true);
    setActionError('');
    try {
      if (confirmAction === 'selesai') {
        await api.post(`/po-orders/complete/${confirmOrder.id}`);
        // Open WA with completion message
        const phone = formatPhone(confirmOrder.customer_phone);
        const msg = WA_SELESAI(confirmOrder.customer_name, confirmOrder.product_name, confirmOrder.quantity);
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
      } else {
        await api.patch(`/po-orders/updateStatus/${confirmOrder.id}`, { status: confirmAction });
        if (confirmAction === 'dibatalkan') {
          const phone = formatPhone(confirmOrder.customer_phone);
          const msg = WA_BATAL(confirmOrder.customer_name, confirmOrder.product_name);
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
        }
      }
      setConfirmOrder(null);
      setConfirmAction(null);
      fetchOrders();
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Gagal mengupdate pesanan');
    } finally {
      setActionLoading(false);
    }
  };

  const counts = {
    all: orders.length,
    menunggu: orders.filter((o) => o.status === 'menunggu').length,
    diproses: orders.filter((o) => o.status === 'diproses').length,
    siap: orders.filter((o) => o.status === 'siap').length,
    selesai: orders.filter((o) => o.status === 'selesai').length,
    dibatalkan: orders.filter((o) => o.status === 'dibatalkan').length,
  };

  const CONFIRM_LABELS: Record<string, { title: string; desc: string; btn: string; btnColor: string }> = {
    diproses:   { title: 'Proses Pesanan',    desc: 'Tandai pesanan ini sudah diorder ke supplier?', btn: 'Proses', btnColor: 'bg-blue-600 hover:bg-blue-700' },
    siap:       { title: 'Tandai Siap Kirim', desc: 'Tandai barang sudah tiba dan siap dikirim ke customer?', btn: 'Tandai Siap', btnColor: 'bg-emerald-600 hover:bg-emerald-700' },
    selesai:    { title: 'Selesaikan Pesanan',desc: 'Selesaikan pesanan ini? Penjualan otomatis dibuat dan WA dikirim ke customer.', btn: 'Selesaikan & Kirim WA', btnColor: 'bg-emerald-600 hover:bg-emerald-700' },
    dibatalkan: { title: 'Batalkan Pesanan',  desc: 'Batalkan pesanan ini? WA pemberitahuan akan dikirim ke customer.', btn: 'Batalkan & Kirim WA', btnColor: 'bg-red-600 hover:bg-red-700' },
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pesanan PO</h1>
          <p className="text-sm text-gray-500 mt-0.5">Kelola pesanan pre-order dari pelanggan</p>
        </div>
        <button onClick={fetchOrders}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {([
          ['all', 'Semua', counts.all],
          ['menunggu', 'Menunggu', counts.menunggu],
          ['diproses', 'Diproses', counts.diproses],
          ['siap', 'Siap Kirim', counts.siap],
          ['selesai', 'Selesai', counts.selesai],
          ['dibatalkan', 'Dibatalkan', counts.dibatalkan],
        ] as const).map(([val, label, count]) => (
          <button key={val} onClick={() => setFilterStatus(val)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition ${
              filterStatus === val ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}>
            {label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${filterStatus === val ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Orders list */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <svg className="animate-spin w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Memuat pesanan...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <svg className="w-14 h-14 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="font-medium text-gray-500">Tidak ada pesanan PO</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((order) => {
            const cfg = STATUS_CONFIG[order.status];
            const canEdit = !['selesai', 'dibatalkan'].includes(order.status);
            return (
              <div key={order.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    {/* Left info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        {order.group_id && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-orange-50 text-orange-600 border border-orange-100">
                            🏷️ {order.group_id}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">#{order.id} · {formatDate(order.created_at)}</span>
                      </div>
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">{order.product_name}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">SKU: {order.sku}</p>
                    </div>

                    {/* Price */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-gray-400">Total Estimasi</p>
                      <p className="font-bold text-gray-800">{rupiahFormat(order.selling_price * order.quantity)}</p>
                      <p className="text-xs text-gray-400">{order.quantity} pcs × {rupiahFormat(order.selling_price)}</p>
                    </div>
                  </div>

                  {/* Customer info */}
                  <div className="mt-3 pt-3 border-t border-gray-50 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-medium">{order.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <a href={`https://wa.me/${formatPhone(order.customer_phone)}`} target="_blank" rel="noreferrer"
                        className="text-emerald-600 hover:underline font-medium">
                        {order.customer_phone}
                      </a>
                    </div>
                  </div>

                  {order.note && (
                    <div className="mt-2 px-3 py-2 bg-gray-50 rounded-lg text-xs text-gray-500 italic">
                      📝 {order.note}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {/* Edit */}
                    {canEdit && (
                      <button onClick={() => openEdit(order)}
                        className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-50 transition">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Edit
                      </button>
                    )}

                    {/* Status transitions */}
                    {order.status === 'menunggu' && (
                      <button onClick={() => openConfirm(order, 'diproses')}
                        className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-medium transition">
                        🔄 Proses
                      </button>
                    )}
                    {order.status === 'diproses' && (
                      <button onClick={() => openConfirm(order, 'siap')}
                        className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-medium transition">
                        ✅ Tandai Siap
                      </button>
                    )}
                    {order.status === 'siap' && (
                      <button onClick={() => openConfirm(order, 'selesai')}
                        className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-medium transition">
                        ✅ Selesaikan
                      </button>
                    )}

                    {/* Cancel */}
                    {['menunggu', 'diproses', 'siap'].includes(order.status) && (
                      <button onClick={() => openConfirm(order, 'dibatalkan')}
                        className="flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-xl text-xs font-medium transition">
                        ❌ Batalkan
                      </button>
                    )}

                    {/* Direct WA chat */}
                    <a href={`https://wa.me/${formatPhone(order.customer_phone)}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 border border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-xl text-xs font-medium transition ml-auto">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Chat WA
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {editOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">Edit Pesanan #{editOrder.id}</h2>
              <button onClick={() => setEditOrder(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleEditSave} className="px-6 py-5 space-y-4">
              {editError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{editError}</div>
              )}
              <div className="bg-orange-50 rounded-xl px-4 py-3 text-sm text-orange-700">
                <span className="font-medium">{editOrder.product_name}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nama Customer</label>
                  <input type="text" required value={editForm.customer_name}
                    onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">No. WhatsApp</label>
                  <input type="tel" required value={editForm.customer_phone}
                    onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah (qty)</label>
                <input type="number" min={1} required value={editForm.quantity}
                  onChange={(e) => setEditForm({ ...editForm, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
                <textarea rows={2} value={editForm.note}
                  onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditOrder(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
                  Batal
                </button>
                <button type="submit" disabled={editSaving}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium disabled:opacity-60 transition">
                  {editSaving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Action Modal */}
      {confirmOrder && confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="px-6 py-5">
              <h2 className="text-base font-semibold text-gray-800 mb-1">{CONFIRM_LABELS[confirmAction].title}</h2>
              <p className="text-sm text-gray-500 mb-1">{CONFIRM_LABELS[confirmAction].desc}</p>
              <div className="mt-3 bg-gray-50 rounded-xl px-4 py-3 text-sm space-y-1">
                <p className="font-medium text-gray-700">{confirmOrder.product_name}</p>
                <p className="text-gray-500">{confirmOrder.customer_name} · {confirmOrder.customer_phone}</p>
                <p className="text-gray-500">Qty: {confirmOrder.quantity} pcs</p>
              </div>
              {actionError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{actionError}</div>
              )}
            </div>
            <div className="flex gap-3 px-6 pb-5">
              <button onClick={() => { setConfirmOrder(null); setConfirmAction(null); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
                Batal
              </button>
              <button onClick={handleConfirmAction} disabled={actionLoading}
                className={`flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-medium disabled:opacity-60 transition ${CONFIRM_LABELS[confirmAction].btnColor}`}>
                {actionLoading ? 'Memproses...' : CONFIRM_LABELS[confirmAction].btn}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
